import { Component, OnDestroy, OnInit } from "@angular/core";
import { AbstractControl, FormGroup, Validators } from "@angular/forms";
import { expandAnimation } from "src/app/animations/expand";
import { FormService } from "src/app/services/form.service";
import { PriceService } from "src/app/services/price.service";
import { TrackingService } from "src/app/services/tracking.service";
import {
  Client,
  PackageResponse,
  PackageTagEnum,
  PropertyEnum,
  PackageTypeEnum,
  AvailableTimeSlotsRequest,
  CalendarDayTimeSlotResponse,
  CalendarTimeSlotResponse,
} from "src/domain/client";
import { PageBaseComponent } from "../../base/page-base/page-base.component";
import { PackageProductMapPipe } from "src/app/pipes/package-product-map.pipe";
import { debounceTime, filter } from "rxjs/operators";
@Component({
  selector: "app-time-selection",
  templateUrl: "./time-selection.component.html",
  animations: [expandAnimation],
})
export class TimeSelectionComponent
  extends PageBaseComponent
  implements OnInit, OnDestroy
{
  public PackageTagEnum = PackageTagEnum;
  public PropertyEnum = PropertyEnum;

  constructor(
    public formService: FormService,
    public priceService: PriceService,
    private trackingService: TrackingService,
    private packageProductMapPipe: PackageProductMapPipe
  ) {
    super();
    this.formService.timeSelectionForm.markAsUntouched();
    this.formService.timeSelectionForm.markAsPristine();
    this.formService.previousFormValue =
      this.formService.timeSelectionForm.value;
  }
  ngOnDestroy(): void {
    if (this.state === this.ComponentStateEnum.InitializationFailed) {
      this.formService.availableTimesResponse = null;
      this.formService.selectedWorkshopDate = null;
      this.formService.selectedWorkshopTime = null;
    }
    if (!this.formService.selectedDropoffOption) return;
    if (!this.date?.pristine || !this.dropoffOption?.pristine)
      this.trackingService.trackDateSelected(
        this.date.value,
        this.formService.selectedDropoffOption?.description?.header,
        this.formService.selectedDropoffOption.subPackages.map((x) => x.name)
      );
    this.cleanUp();
  }

  ngOnInit(): void {
    this.trackListView();
    if (!!this.formService.availableTimesResponse) {
      this.date.setValue(this.formService.selectedWorkshopDate?.date ?? null);
      this.evaluateForm();
      this.state = this.ComponentStateEnum.Initialized;

      return;
    }
    var selectedPackages = this.formService.selectedBasePackages.concat(
      this.formService.selectedAdditionalPackages
    );
    this.state = this.ComponentStateEnum.Loading;
    new Client()
      .apiV2CalendarAvailableTimeSlots(
        AvailableTimeSlotsRequest.fromJS({
          dealerNumber: this.formService.selectedWorkshop.number,
          chassiNumber: this.formService.carInfoResponse.chassiNumber,
          odometer: this.formService.milageValue * 10,
          selectedPackages: selectedPackages,
        })
      )
      .then((response: CalendarDayTimeSlotResponse[]) => {
        this.formService.availableTimesResponse = response;

        this.formService.initSpecificFormFromPackages(
          this.formService.timeSelectionForm,
          this.formService.basePackagesResponse.filter(
            (p) => p.type == PackageTypeEnum.DropoffOption
          )
        );
        if (!!response.length) {
          this.state = this.ComponentStateEnum.Initialized;
          this.date.setValue(response[0].date);
          this.formService.selectedWorkshopDate = response[0];
        }
        if (!response.length)
          this.state = this.ComponentStateEnum.InitializationFailed;
      })
      .catch((error) => {
        console.error(error);
        this.state = this.ComponentStateEnum.ApiCallFailed;
      });

    this.subscription = this.dropoffOption.valueChanges
      .pipe(
        debounceTime(100),
        filter((opt) => !!opt)
      )
      .subscribe(() => this.evaluateForm());
  }

  onSelectDay(date: Date): void {
    let newOption = null;
    if (!!this.selectedDate && !!this.selectedDropoffOption)
      newOption = this.selectedDate.availableDropoffoptionIds?.includes(
        this.selectedDropoffOption.id
      )
        ? this.selectedDropoffOption
        : null;
    this.formService.timeSelectionForm.patchValue({
      date,
      dropoffOption: newOption,
      time: null,
      calendarPackage: null,
    });

    this.formService.selectedWorkshopDate = this.selectedDate;
    this.date.markAsDirty();
  }

  onTimeSelected(selectedTime: string): void {
    const hours = parseInt(selectedTime.split(":")[0], 10);
    const minutes = parseInt(selectedTime.split(":")[1], 10);
    const newDate = this.date.value as Date;
    newDate.setHours(hours, minutes);
    this.formService.timeSelectionForm.patchValue({
      date: newDate,
      time: selectedTime === this.time.value ? null : selectedTime,
    });

    this.formService.selectedWorkshopTime =
      this.formService.selectedWorkshopDate?.timeSlots?.find(
        (t: CalendarTimeSlotResponse) => t.slot === selectedTime
      );
  }

  timeSelectionParentForm(id: string): FormGroup<any> {
    return this.formService.timeSelectionForm.get(id) as FormGroup<any>;
  }

  get dropoffOptions(): PackageResponse[] {
    return !!this.formService.basePackagesResponse
      ? this.formService.basePackagesResponse.filter(
          (o: PackageResponse) =>
            !!this.selectedDate &&
            o.type == PackageTypeEnum.DropoffOption &&
            this.selectedDate.availableDropoffoptionIds.includes(o.id)
        )
      : [];
  }
  get workshopDates(): CalendarDayTimeSlotResponse[] {
    return !!this.formService.availableTimesResponse
      ? this.formService.availableTimesResponse
      : [];
  }
  get date(): AbstractControl {
    return this.formService.timeSelectionForm.get("date");
  }
  get time(): AbstractControl {
    return this.formService.timeSelectionForm.get("time");
  }
  get dropoffOption(): AbstractControl {
    return this.formService.timeSelectionForm.get("selectedContainer");
  }

  get selectedDate(): CalendarDayTimeSlotResponse {
    if (!this.date.value) return undefined;
    return this.workshopDates.find(
      (d: CalendarDayTimeSlotResponse) =>
        d.date.toDateString() === this.date.value.toDateString()
    );
  }

  get selectedDropoffOption(): PackageResponse {
    return (
      !!this.selectedDate &&
      this.formService.basePackagesResponse?.find(
        (d: PackageResponse) => d.id === this.dropoffOption.value
      )
    );
  }

  get selectedDayAvailableHours(): CalendarTimeSlotResponse[] {
    if (!this.selectedDate) return [];
    return this.selectedDate.timeSlots;
  }

  evaluateForm(): void {
    if (!this.selectedDate) return;
    if (!this.selectedDropoffOption) return;
    const dropoff = this.dropoffOptions.find(
      (o) => o.id === this.selectedDropoffOption.id
    );
    if (!dropoff.properties.includes(PropertyEnum.TimeSlot)) {
      this.time.clearValidators();
      this.time.updateValueAndValidity();
      return;
    }
    this.time.setValidators(Validators.required);
    this.time.updateValueAndValidity();
    this.formService.timeSelectionForm.updateValueAndValidity();
    return;
  }

  private trackListView(): void {
    this.trackingService.trackItemListViewed(
      "Step6",
      [].concat.apply(
        [],
        this.formService.basePackagesResponse
          .filter(
            (x) =>
              x.type == PackageTypeEnum.CheckInOption ||
              x.type == PackageTypeEnum.DropoffOption ||
              x.type == PackageTypeEnum.Mobility
          )
          .map((x) =>
            [
              this.packageProductMapPipe.transform(
                x,
                this.formService.carInfoResponse?.brandName
              ),
            ].concat(
              x.subPackages.map((sp) =>
                this.packageProductMapPipe.transform(
                  sp,
                  this.formService.carInfoResponse?.brandName
                )
              )
            )
          )
      )
    );
  }
}
